import { useMemo, useState } from 'react'

import { useWindowSize } from '@tabeeb/shared/utils/hooks'
import { mimeTypes } from '@collaborate-health/uikit/constants'

import PdfViewer from '@collaborate-health/uikit/components/PdfViewer'
import ImageViewer, { ImageViewerProps } from '@collaborate-health/uikit/components/ImageViewer'
import { usePdfViewer } from '@collaborate-health/uikit'

export interface CertificatesImageViewerProps {
  open: boolean
  src: string | null | undefined // base64 or blob storage URL
  onClose: () => void
}

const CertificatesImageViewer: React.FC<CertificatesImageViewerProps> = ({ open, src, onClose }) => {
  const { height } = useWindowSize()
  const [{ height: pdfHeight, width: pdfWidth }, setCurrentPdfDimensions] = useState({ height: 0, width: 0 })

  const { current, total, onNextPageClick, onPreviousPageClick, onDocumentLoaded } = usePdfViewer()

  const type = useMemo<'image' | 'pdf' | undefined>(() => {
    if (!src) {
      return undefined
    }

    try {
      const url = new URL(src)
      if (url.pathname.toLowerCase().endsWith('.pdf')) {
        return 'pdf'
      }
    } catch {
      if (src.startsWith('data:application/pdf')) {
        return 'pdf'
      }
    }

    return 'image'
  }, [src])

  const isPdf = type === 'pdf'

  const slides = useMemo<ImageViewerProps['slides']>(() => {
    if (!src) {
      return []
    }

    return [
      {
        src,
        ...(isPdf && { type: mimeTypes.pdf }),
        download: { url: src, filename: isPdf ? 'certificate.pdf' : 'certificate' },
      },
    ]
  }, [src, isPdf])

  const customSlideRenderProps = useMemo<ImageViewerProps['customSlideRenderProps']>(() => {
    return {
      customSlideRender: {
        // eslint-disable-next-line react/no-unstable-nested-components
        slide: ({ slide }) => {
          if (slide.type !== mimeTypes.pdf) {
            return undefined
          }

          return (
            <PdfViewer
              src={slide.src}
              current={current}
              total={total}
              height={height - 100}
              showNavigation={false}
              onPageLoaded={setCurrentPdfDimensions}
              onDocumentLoaded={onDocumentLoaded}
              onNextPageClick={onNextPageClick}
              onPreviousPageClick={onPreviousPageClick}
            />
          )
        },
      },
      nextPageClick: onNextPageClick,
      nextPageClickAvailable: current! < total!,
      previousPageClick: onPreviousPageClick,
      previousPageClickAvailable: current !== 1,
    }
  }, [current, height, onDocumentLoaded, onNextPageClick, onPreviousPageClick, total])

  return (
    <ImageViewer
      open={open}
      onClose={onClose}
      customSlideRenderProps={isPdf ? customSlideRenderProps : undefined}
      slides={slides.map((e) => ({
        ...e,
        ...(pdfHeight && { height: pdfHeight }),
        ...(pdfWidth && { width: pdfWidth }),
      }))}
    />
  )
}

export default CertificatesImageViewer

